import {
  BarChartOutlined,
  CalendarOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  FileTextOutlined,
  QuestionCircleOutlined,
  StarOutlined,
  UserOutlined,
} from '@ant-design/icons';
import {
  Avatar,
  Button,
  Card,
  Col,
  Drawer,
  Empty,
  List,
  message,
  Progress,
  Row,
  Space,
  Statistic,
  Tabs,
  Tag,
  Timeline,
  Typography,
} from 'antd';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import type React from 'react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import api from '../config/api';

dayjs.extend(relativeTime);

const { TabPane } = Tabs;
const { Title, Text } = Typography;

interface FeedbackFormDetailsProps {
  visible: boolean;
  onClose: () => void;
  form: any;
  onRefresh: () => void;
}

const FeedbackFormDetails: React.FC<FeedbackFormDetailsProps> = ({
  visible,
  onClose,
  form,
  onRefresh,
}) => {
  const { t } = useTranslation();
  const [responses, setResponses] = useState<any[]>([]);
  const [loadingResponses, setLoadingResponses] = useState(false);
  const [analytics, setAnalytics] = useState<any>(null);
  const [loadingAnalytics, setLoadingAnalytics] = useState(false);

  useEffect(() => {
    if (visible && form) {
      fetchResponses();
      fetchAnalytics();
    }
  }, [visible, form]);

  const fetchResponses = async () => {
    if (!form) return;

    setLoadingResponses(true);
    try {
      const response = await api.get('/feedback/responses', {
        params: { formId: form.id, limit: 10 },
      });
      setResponses(response.data.data || []);
    } catch (error) {
      message.error(t('feedbackFormDetails.responsesLoadFailed'));
    } finally {
      setLoadingResponses(false);
    }
  };

  const fetchAnalytics = async () => {
    if (!form) return;

    setLoadingAnalytics(true);
    try {
      const response = await api.get(`/feedback/forms/${form.id}/analytics`);
      setAnalytics(response.data);
    } catch (error) {
      message.error(t('feedbackFormDetails.analyticsLoadFailed'));
    } finally {
      setLoadingAnalytics(false);
    }
  };

  if (!form) return null;

  const getQuestionTypeIcon = (type: string) => {
    switch (type) {
      case 'TEXT':
        return <FileTextOutlined />;
      case 'RATING':
        return <StarOutlined />;
      case 'SCALE':
        return <BarChartOutlined />;
      default:
        return <QuestionCircleOutlined />;
    }
  };

  const getQuestionTypeColor = (type: string) => {
    switch (type) {
      case 'TEXT':
        return 'blue';
      case 'RATING':
        return 'gold';
      case 'SINGLE_CHOICE':
        return 'green';
      case 'MULTIPLE_CHOICE':
        return 'purple';
      case 'SCALE':
        return 'orange';
      case 'YES_NO':
        return 'cyan';
      default:
        return 'default';
    }
  };

  const renderQuestionAnalytics = (question: any) => {
    if (!question.data) return null;

    switch (question.type) {
      case 'RATING':
      case 'SCALE':
        return (
          <div>
            <Row gutter={16}>
              <Col span={8}>
                <Statistic
                  title={t('feedbackFormDetails.statAverage')}
                  value={question.data.average}
                  precision={1}
                  valueStyle={{ color: '#3f8600' }}
                />
              </Col>
              <Col span={8}>
                <Statistic
                  title={t('feedbackFormDetails.statMin')}
                  value={question.data.min}
                  valueStyle={{ color: '#cf1322' }}
                />
              </Col>
              <Col span={8}>
                <Statistic
                  title={t('feedbackFormDetails.statMax')}
                  value={question.data.max}
                  valueStyle={{ color: '#1890ff' }}
                />
              </Col>
            </Row>
          </div>
        );

      case 'YES_NO': {
        const total = (question.data.yes || 0) + (question.data.no || 0);
        const yesPercent = total > 0 ? (question.data.yes / total) * 100 : 0;
        return (
          <div>
            <Progress
              percent={yesPercent}
              success={{ percent: yesPercent }}
              format={() => t('feedbackFormDetails.yesCount', { count: question.data.yes || 0 })}
            />
            <Text type="secondary">
              {t('feedbackFormDetails.noCount', { count: question.data.no || 0 })}
            </Text>
          </div>
        );
      }

      case 'SINGLE_CHOICE':
      case 'MULTIPLE_CHOICE':
        return (
          <List
            size="small"
            dataSource={question.data.topChoices || []}
            renderItem={(item: any) => (
              <List.Item>
                <Text>{item.choice}</Text>
                <Tag color="blue">{item.count}</Tag>
              </List.Item>
            )}
          />
        );

      case 'TEXT':
        return (
          <div>
            <Text type="secondary">
              {t('feedbackFormDetails.totalTextResponses', {
                count: question.data.totalResponses,
              })}
            </Text>
            {question.data.samples && question.data.samples.length > 0 && (
              <List
                size="small"
                dataSource={question.data.samples.slice(0, 3)}
                renderItem={(item: string) => (
                  <List.Item>
                    <Text ellipsis>{item}</Text>
                  </List.Item>
                )}
              />
            )}
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <Drawer title={form?.title} placement="right" width={800} onClose={onClose} open={visible}>
      <Tabs defaultActiveKey="1">
        <TabPane tab={t('feedbackFormDetails.tabOverview')} key="1">
          <Space direction="vertical" style={{ width: '100%' }} size="large">
            <Card>
              <Row gutter={16}>
                <Col span={12}>
                  <Statistic
                    title={t('feedbackFormDetails.statTotalQuestions')}
                    value={form?.questions?.length || form?._count?.questions || 0}
                    prefix={<QuestionCircleOutlined />}
                  />
                </Col>
                <Col span={12}>
                  <Statistic
                    title={t('feedbackFormDetails.statTotalResponses')}
                    value={form?._count?.responses || 0}
                    prefix={<UserOutlined />}
                    valueStyle={{ color: '#3f8600' }}
                  />
                </Col>
              </Row>
            </Card>

            <Card title={t('feedbackFormDetails.formDetails')}>
              <List>
                <List.Item>
                  <Text strong>{t('feedbackFormDetails.labelEvent')}</Text>
                  <Tag color="blue">{form?.event?.title}</Tag>
                </List.Item>
                <List.Item>
                  <Text strong>{t('feedbackFormDetails.labelStatus')}</Text>
                  {form?.isActive ? (
                    <Tag color="success" icon={<CheckCircleOutlined />}>
                      {t('feedbackFormDetails.statusActive')}
                    </Tag>
                  ) : (
                    <Tag color="default">{t('feedbackFormDetails.statusInactive')}</Tag>
                  )}
                </List.Item>
                {form?.description && (
                  <List.Item>
                    <Text strong>{t('feedbackFormDetails.labelDescription')}</Text>
                    <Text>{form.description}</Text>
                  </List.Item>
                )}
                {form?.openAt && (
                  <List.Item>
                    <Text strong>{t('feedbackFormDetails.labelOpensAt')}</Text>
                    <Text>{dayjs(form.openAt).format('YYYY-MM-DD HH:mm')}</Text>
                  </List.Item>
                )}
                {form?.closeAt && (
                  <List.Item>
                    <Text strong>{t('feedbackFormDetails.labelClosesAt')}</Text>
                    <Text>{dayjs(form.closeAt).format('YYYY-MM-DD HH:mm')}</Text>
                  </List.Item>
                )}
                <List.Item>
                  <Text strong>{t('feedbackFormDetails.labelCreated')}</Text>
                  <Text>{dayjs(form?.createdAt).format('YYYY-MM-DD HH:mm')}</Text>
                </List.Item>
              </List>
            </Card>
          </Space>
        </TabPane>

        <TabPane tab={t('feedbackFormDetails.tabQuestions')} key="2">
          <List
            dataSource={form?.questions || []}
            renderItem={(question: any, index: number) => (
              <List.Item>
                <Card style={{ width: '100%' }}>
                  <Space direction="vertical" style={{ width: '100%' }}>
                    <Space>
                      <Avatar>{index + 1}</Avatar>
                      <Text strong>{question.question}</Text>
                      <Tag color={getQuestionTypeColor(question.type)}>
                        {getQuestionTypeIcon(question.type)} {question.type}
                      </Tag>
                      {question.required && (
                        <Tag color="red">{t('feedbackFormDetails.required')}</Tag>
                      )}
                    </Space>
                    {question.options && (
                      <div style={{ marginLeft: 40 }}>
                        <Text type="secondary">{t('feedbackFormDetails.labelOptions')}</Text>
                        <List
                          size="small"
                          dataSource={question.options}
                          renderItem={(option: string) => <List.Item>• {option}</List.Item>}
                        />
                      </div>
                    )}
                    {question.scaleMin !== null && question.scaleMax !== null && (
                      <div style={{ marginLeft: 40 }}>
                        <Text type="secondary">
                          {t('feedbackFormDetails.scaleRange', {
                            min: question.scaleMin,
                            max: question.scaleMax,
                          })}
                        </Text>
                        {question.scaleLabels && (
                          <div>
                            <Text type="secondary">
                              ({question.scaleLabels.min} - {question.scaleLabels.max})
                            </Text>
                          </div>
                        )}
                      </div>
                    )}
                  </Space>
                </Card>
              </List.Item>
            )}
          />
        </TabPane>

        <TabPane tab={t('feedbackFormDetails.tabAnalytics')} key="3">
          {loadingAnalytics ? (
            <Card loading />
          ) : analytics ? (
            <Space direction="vertical" style={{ width: '100%' }} size="large">
              <Card>
                <Row gutter={16}>
                  <Col span={12}>
                    <Statistic
                      title={t('feedbackFormDetails.statResponseRate')}
                      value={analytics.responseRate}
                      precision={1}
                      suffix="%"
                      valueStyle={{ color: '#3f8600' }}
                    />
                  </Col>
                  <Col span={12}>
                    <Statistic
                      title={t('feedbackFormDetails.statTotalResponses')}
                      value={analytics.totalResponses}
                      prefix={<UserOutlined />}
                    />
                  </Col>
                </Row>
              </Card>

              <Title level={5}>{t('feedbackFormDetails.questionAnalytics')}</Title>
              {analytics.questions?.map((question: any, index: number) => (
                <Card
                  key={question.id}
                  title={
                    <Space>
                      <Avatar size="small">{index + 1}</Avatar>
                      <Text>{question.question}</Text>
                      <Tag color={getQuestionTypeColor(question.type)}>{question.type}</Tag>
                    </Space>
                  }
                  extra={
                    <Text type="secondary">
                      {t('feedbackFormDetails.answersCount', {
                        count: question.totalAnswers,
                      })}
                    </Text>
                  }
                >
                  {renderQuestionAnalytics(question)}
                </Card>
              ))}
            </Space>
          ) : (
            <Empty description={t('feedbackFormDetails.noAnalytics')} />
          )}
        </TabPane>

        <TabPane tab={t('feedbackFormDetails.tabRecentResponses')} key="4">
          {loadingResponses ? (
            <Card loading />
          ) : responses.length > 0 ? (
            <Timeline>
              {responses.map((response: any) => (
                <Timeline.Item
                  key={response.id}
                  dot={<UserOutlined style={{ fontSize: '16px' }} />}
                >
                  <Card size="small">
                    <Space direction="vertical">
                      <Space>
                        {response.user ? (
                          <Tag icon={<UserOutlined />}>
                            {response.user.firstName} {response.user.lastName}
                          </Tag>
                        ) : response.email ? (
                          <Tag>{response.email}</Tag>
                        ) : (
                          <Tag>{t('feedbackFormDetails.anonymous')}</Tag>
                        )}
                        <Text type="secondary">{dayjs(response.submittedAt).fromNow()}</Text>
                      </Space>
                      <Text type="secondary">
                        {t('feedbackFormDetails.answersSubmitted', {
                          count: response.answers?.length || 0,
                        })}
                      </Text>
                    </Space>
                  </Card>
                </Timeline.Item>
              ))}
            </Timeline>
          ) : (
            <Empty description={t('feedbackFormDetails.noResponses')} />
          )}
        </TabPane>
      </Tabs>
    </Drawer>
  );
};

export default FeedbackFormDetails;
